import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Download, FileSignature } from "lucide-react";
import { SignatureDialog } from "@/components/proposals/SignatureDialog";
import { CoverBlock } from "@/components/proposals/blocks/CoverBlock";
import { TextBlock } from "@/components/proposals/blocks/TextBlock";
import { ImageBlock } from "@/components/proposals/blocks/ImageBlock";
import { LineItemsTable } from "@/components/proposals/blocks/LineItemsTable";
import { SignatureBlock } from "@/components/proposals/blocks/SignatureBlock";
import type { ProposalDocument, Block } from "@/types/proposals";
import { toast } from "sonner";

export default function PublicProposal() {
  const { shareId } = useParams();
  const [proposal, setProposal] = useState<ProposalDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);

  useEffect(() => {
    if (shareId) {
      loadProposal();
      trackView();
    }
  }, [shareId]);

  const loadProposal = async () => {
    try {
      const { data, error } = await supabase
        .from("proposal_documents")
        .select("*")
        .eq("public_share_id", shareId)
        .single();

      if (error) throw error;
      setProposal(data as unknown as ProposalDocument);
    } catch (error) {
      console.error("Error loading proposal:", error);
      toast.error("Failed to load proposal");
    } finally {
      setLoading(false);
    }
  };

  const trackView = async () => {
    try {
      await supabase.from("proposal_activities").insert({
        proposal_id: shareId,
        activity_type: "viewed",
        ip_address: "unknown",
      });
    } catch (error) {
      console.error("Error tracking view:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading proposal...</p>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-12 text-center">
          <p className="text-lg">Proposal not found</p>
        </Card>
      </div>
    );
  }

  const pages = proposal.layout_filled_json.pages;
  const currentPage = pages[currentPageIndex];
  const isSigned = proposal.status === "signed";

  const renderBlock = (block: Block) => {
    const props = {
      block,
      onUpdate: () => {},
      onSelect: () => {},
    };

    switch (block.type) {
      case "cover":
        return <CoverBlock key={block.id} {...props} />;
      case "text":
        return <TextBlock key={block.id} {...props} />;
      case "image":
        return <ImageBlock key={block.id} {...props} />;
      case "line_items_table":
        return <LineItemsTable key={block.id} {...props} />;
      case "signature":
        return <SignatureBlock key={block.id} {...props} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <div className="border-b bg-card px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{proposal.title}</h1>
            {isSigned && (
              <p className="text-sm text-green-500 mt-1">✓ Signed by {proposal.signed_by_name}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            {!isSigned && (
              <Button
                onClick={() => setSignatureDialogOpen(true)}
                className="bg-action hover:bg-action-hover"
                size="sm"
              >
                <FileSignature className="w-4 h-4 mr-2" />
                Sign Proposal
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="bg-white dark:bg-card shadow-lg p-12 min-h-[800px]">
            {currentPage && (
              <div className="space-y-6">
                {currentPage.blocks
                  .sort((a, b) => a.order - b.order)
                  .map((block) => renderBlock(block))}
              </div>
            )}
          </Card>

          {/* Page Navigation */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button
              variant="outline"
              onClick={() => setCurrentPageIndex(Math.max(0, currentPageIndex - 1))}
              disabled={currentPageIndex === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {currentPageIndex + 1} of {pages.length}
            </span>
            <Button
              variant="outline"
              onClick={() => setCurrentPageIndex(Math.min(pages.length - 1, currentPageIndex + 1))}
              disabled={currentPageIndex === pages.length - 1}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      <SignatureDialog
        open={signatureDialogOpen}
        onOpenChange={setSignatureDialogOpen}
        proposalId={proposal.id}
        onSigned={loadProposal}
      />
    </div>
  );
}
